import { useState } from 'react';
import { AppLayout } from '@/components/layout/AppLayout';
import { SEO } from '@/components/seo/SEO';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Spinner } from '@/components/ui/spinner';
import { 
  Plus, 
  FileText, 
  AlertTriangle, 
  CheckCircle2, 
  Clock,
  Euro,
  ArrowLeft
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTrafficFines, TrafficFine, getDaysRemaining } from '@/hooks/useTrafficFines';
import { FineCard } from '@/components/fines/FineCard';
import { FineUploadDialog } from '@/components/fines/FineUploadDialog';
import { FineDetailSheet } from '@/components/fines/FineDetailSheet';
import { DriverDesignationDialog } from '@/components/fines/DriverDesignationDialog';

export default function FleetFines() {
  const { fines, isLoading, stats } = useTrafficFines();
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [selectedFine, setSelectedFine] = useState<TrafficFine | null>(null);
  const [fineForDesignation, setFineForDesignation] = useState<TrafficFine | null>(null);
  const [activeTab, setActiveTab] = useState('all');

  // Filter fines by tab
  const filteredFines = fines.filter(fine => {
    switch (activeTab) {
      case 'pending':
        return fine.status === 'received' || fine.status === 'pending_designation';
      case 'urgent':
        const days = getDaysRemaining(fine.designation_deadline);
        return (fine.status === 'received' || fine.status === 'pending_designation') 
          && days !== null && days <= 15;
      case 'paid':
        return fine.status === 'paid';
      default:
        return true;
    }
  });

  // Count urgent fines
  const urgentCount = fines.filter(fine => {
    const days = getDaysRemaining(fine.designation_deadline);
    return (fine.status === 'received' || fine.status === 'pending_designation') 
      && days !== null && days <= 15;
  }).length;

  return (
    <AppLayout>
      <SEO 
        title="Gestion des amendes | EasyFinder" 
        description="Gérez les contraventions de votre flotte"
      />

      <div className="p-4 md:p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" asChild>
              <Link to="/fleet">
                <ArrowLeft className="h-5 w-5" />
              </Link>
            </Button>
            <div>
              <h1 className="text-2xl font-bold">Amendes & Contraventions</h1>
              <p className="text-muted-foreground">
                Suivi et désignation des conducteurs
              </p>
            </div>
          </div>
          <Button onClick={() => setShowUploadDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Ajouter une amende
          </Button>
        </div>

        {/* Stats cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-muted">
                  <FileText className="h-5 w-5 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total}</p>
                  <p className="text-sm text-muted-foreground">Total</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={urgentCount > 0 ? "border-destructive/50" : ""}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${urgentCount > 0 ? 'bg-destructive/10' : 'bg-muted'}`}>
                  <AlertTriangle className={`h-5 w-5 ${urgentCount > 0 ? 'text-destructive' : 'text-muted-foreground'}`} />
                </div>
                <div>
                  <p className="text-2xl font-bold">{urgentCount}</p>
                  <p className="text-sm text-muted-foreground">Urgentes</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30">
                  <Clock className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.pending}</p>
                  <p className="text-sm text-muted-foreground">En attente</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-muted">
                  <Euro className="h-5 w-5 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.totalPendingAmount.toFixed(0)}€</p>
                  <p className="text-sm text-muted-foreground">À payer</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Fines list with tabs */}
        <Card>
          <CardHeader className="pb-0">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="all">
                  Toutes
                  <Badge variant="secondary" className="ml-2">{fines.length}</Badge>
                </TabsTrigger>
                <TabsTrigger value="pending">
                  En attente
                  <Badge variant="secondary" className="ml-2">{stats.pending}</Badge>
                </TabsTrigger>
                <TabsTrigger value="urgent" className="text-destructive">
                  Urgentes
                  {urgentCount > 0 && (
                    <Badge variant="destructive" className="ml-2">{urgentCount}</Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="paid">
                  Payées
                  <Badge variant="secondary" className="ml-2">{stats.paid}</Badge>
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </CardHeader>
          <CardContent className="pt-4">
            {isLoading ? (
              <div className="py-12 text-center">
                <Spinner className="h-8 w-8 mx-auto" />
              </div>
            ) : filteredFines.length === 0 ? (
              <div className="py-12 text-center">
                <FileText className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground">
                  {activeTab === 'all' 
                    ? "Aucune amende enregistrée" 
                    : "Aucune amende dans cette catégorie"
                  }
                </p>
                {activeTab === 'all' && (
                  <Button 
                    variant="outline" 
                    className="mt-4"
                    onClick={() => setShowUploadDialog(true)}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Ajouter votre première amende
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {filteredFines.map((fine) => (
                  <FineCard
                    key={fine.id}
                    fine={fine}
                    onClick={() => setSelectedFine(fine)}
                    onDesignate={() => setFineForDesignation(fine)}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Upload dialog */}
      <FineUploadDialog
        open={showUploadDialog}
        onOpenChange={setShowUploadDialog}
      />

      {/* Detail sheet */}
      <FineDetailSheet
        open={!!selectedFine}
        onOpenChange={(open) => !open && setSelectedFine(null)}
        fine={selectedFine}
      />

      {/* Designation dialog */}
      {fineForDesignation && (
        <DriverDesignationDialog
          open={!!fineForDesignation}
          onOpenChange={(open) => !open && setFineForDesignation(null)}
          fine={fineForDesignation}
        />
      )}
    </AppLayout>
  );
}
